import { RuleEmployeeCountPreview } from "@/components/apply-to/RuleEmployeeCountPreview.tsx";
import { Datepicker } from "@/components/datepicker/Datepicker.tsx";
import {
  Title,
  useModals,
  Modal,
  Button,
  LinkButtonTo,
  ListItem,
  Multiselect,
  Select,
  SelectItem,
  SelectValueType,
  Stack,
  Text,
} from "@skailer-com/uikit";
import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import { EditRuleSchemaObject, RuleEditModalProps } from "@/pages/settings/roles/role.types.ts";
import useEmployeeSearchInline from "@/queries/employees/use-employees.query.ts";
import { useEmploymentContracts } from "@/queries/employment-contracts.query.ts";
import { useEmploymentTypes } from "@/queries/employment-types.query.ts";
import { useWorkPatternsSearchInline } from "@/queries/use-work-patterns.query.ts";
import {
  useEmployeeReferenceCustomFields,
  useEmployeeSelectCustomFields,
} from "@/queries/use-custom-fields.query.ts";
import { useDepartmentsSearchInline } from "@/queries/use-departments.query.ts";
import { useDivisionsSearchInline } from "@/queries/use-divisitions.query.ts";
import { useLegalEntitiesSearchInline } from "@/queries/use-legal-entities.query.ts";
import { useLocationsSearchInline } from "@/queries/use-locations.query.ts";
import { useJobProfiles } from "@/queries/use-job-profiles.query.ts";
import { useTeamSearchInline } from "@/queries/use-teams.query.ts";
import { EmployeeGenders, getLifecycleStatusSelectItems } from "@/types/employees/employee.types.ts";
import {
  buildDateCondition,
  DateConditionOperator,
  getCalcModeFromDateCondition,
  getPresetFromDateCondition,
  RuleApplySignType,
  RuleConditionType,
  RuleEmploymentDurationCalcMode,
  RuleEmploymentDurationCalcModeItems,
  RuleEmploymentDurationDateConditionItems,
  RuleEmploymentDurationPeriodConditionItems,
  RuleEmploymentDurationPresetItems,
  RuleEmploymentDurationPresetType,
  RuleSignItems,
} from "@/types/role.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, parseISO } from "date-fns";
import { useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

export function RuleEditModal({
  rule,
  onModified,
  onDelete,
  conditionTypes,
  popupTitle,
  forceSign,
  isSingleSelect = false,
  singleSelectConditionTypes = [],
  multiSelectConditionTypes = [],
  canDelete = true,
  applyLabel,
  signItems,
  showEmployeeCountPreview = false,
  ...props
}: RuleEditModalProps) {
  const { t } = useTranslation();
  const { close } = useModals();
  const { id } = { ...props };
  const existingDateCondition = rule?.date_condition;

  const {
    handleSubmit,
    formState: { errors },
    control,
    resetField,
    watch,
    setValue,
  } = useForm({
    mode: "onSubmit",
    resolver: zodResolver(EditRuleSchemaObject),
    defaultValues: {
      condition_ids: rule?.condition_ids ?? [],
      value_ids: rule?.value_ids ?? [],
      sign: forceSign ?? rule?.sign ?? RuleApplySignType.include,
      condition_type: rule?.condition_type ?? RuleConditionType.employee,
      condition_entities: rule?.condition_entities ?? [],
      value_entities: rule?.value_entities ?? [],
      employment_duration_calc_mode: getCalcModeFromDateCondition(existingDateCondition),
      employment_duration_operator: existingDateCondition?.condition ?? "greater_than",
      employment_duration_preset: existingDateCondition
        ? getPresetFromDateCondition(existingDateCondition)
        : RuleEmploymentDurationPresetType.one_month,
      employment_duration_date_value:
        existingDateCondition?.condition_type === "specific_date"
          ? existingDateCondition.date_from
          : null,
    },
  });

  const selectedConditionType = useWatch({ control, name: "condition_type" });
  const employmentDurationCalcMode = useWatch({ control, name: "employment_duration_calc_mode" });

  const divisions = useDivisionsSearchInline(selectedConditionType === RuleConditionType.division);
  const departments = useDepartmentsSearchInline(
    selectedConditionType === RuleConditionType.department
  );
  const { options: jobProfileOptions, isLoading: isLoadingJobProfiles } = useJobProfiles(
    true,
    selectedConditionType === RuleConditionType.job_profile
  );
  const employee = useEmployeeSearchInline(
    [
      RuleConditionType.employee,
      RuleConditionType.employees_with_employee_in_custom_field,
    ].includes(selectedConditionType)
  );
  const locations = useLocationsSearchInline(selectedConditionType == RuleConditionType.location);
  const legalEntities = useLegalEntitiesSearchInline(
    selectedConditionType == RuleConditionType.legal_entity
  );
  const teams = useTeamSearchInline(selectedConditionType == RuleConditionType.team);
  const employmentTypes = useEmploymentTypes(
    selectedConditionType === RuleConditionType.employment_type
  );
  const employmentContracts = useEmploymentContracts(
    selectedConditionType === RuleConditionType.employment_contract
  );
  const workPatterns = useWorkPatternsSearchInline(
    selectedConditionType === RuleConditionType.work_pattern
  );

  const employeeCustomFields = useEmployeeReferenceCustomFields(
    [
      RuleConditionType.custom_field_employee_reference,
      RuleConditionType.employees_in_custom_field,
      RuleConditionType.employees_with_employee_in_custom_field,
      RuleConditionType.employees_with_me_in_custom_field,
    ].includes(selectedConditionType)
  );

  const selectCustomFields = useEmployeeSelectCustomFields(
    selectedConditionType === RuleConditionType.custom_field_select
  );

  const options = useMemo<SelectItem[] | undefined>(() => {
    switch (selectedConditionType) {
      case RuleConditionType.division:
        return divisions.options;
      case RuleConditionType.department:
        return departments.options;
      case RuleConditionType.job_profile:
        return jobProfileOptions;
      case RuleConditionType.employee:
        return employee.options;
      case RuleConditionType.employment_type:
        return employmentTypes.data;
      case RuleConditionType.employment_contract:
        return employmentContracts.data;
      case RuleConditionType.work_pattern:
        return workPatterns.options;
      case RuleConditionType.location:
        return locations.options;
      case RuleConditionType.legal_entity:
        return legalEntities.options;
      case RuleConditionType.team:
        return teams.options;
      case RuleConditionType.lifecycle_status:
        return getLifecycleStatusSelectItems();
      case RuleConditionType.gender:
        return EmployeeGenders.map((x) => ({ ...x, title: t(x.title) }));
      case RuleConditionType.affected_employee_custom_field_employee_reference:
      case RuleConditionType.affected_employee_custom_field_employee_reference_subordinates:
      case RuleConditionType.custom_field_employee_reference:
      case RuleConditionType.employees_in_custom_field:
      case RuleConditionType.employees_with_employee_in_custom_field:
      case RuleConditionType.employees_with_me_in_custom_field:
        return employeeCustomFields.data.map(
          (f) =>
            ({
              id: f.id,
              title: f.title,
            }) as SelectItem
        );
      case RuleConditionType.custom_field_select:
        return selectCustomFields.data.map(
          (f) =>
            ({
              id: f.id,
              title: f.title,
            }) as SelectItem
        );

      default:
        return [] as SelectItem[];
    }
  }, [
    selectedConditionType,
    divisions.options,
    departments.options,
    jobProfileOptions,
    employee.options,
    locations.options,
    legalEntities.options,
    teams.options,
    employmentTypes.data,
    employmentContracts.data,
    workPatterns.options,
    employeeCustomFields.data,
    selectCustomFields.data,
    t,
  ]);

  const watchedConditionIds = useWatch({
    control,
    name: "condition_ids",
  });
  const selectedFieldIds = useMemo(() => watchedConditionIds ?? [], [watchedConditionIds]);

  const valueOptions = useMemo(() => {
    switch (selectedConditionType) {
      case RuleConditionType.custom_field_select: {
        const selectedFields = selectCustomFields.data.filter((f) =>
          selectedFieldIds.includes(f.id)
        );

        return selectedFields.flatMap((f) =>
          (f.options || [])
            .filter((opt) => opt.id !== undefined)
            .map((opt) => ({
              id: opt.id!,
              title: opt.title,
            }))
        );
      }

      case RuleConditionType.employees_with_employee_in_custom_field:
        return employee.options;

      default:
        return [] as SelectItem[];
    }
  }, [selectCustomFields.data, selectedFieldIds, selectedConditionType, employee.options]);

  const onSubmit = handleSubmit((ruleData) => {
    const isEmploymentDuration = ruleData.condition_type === RuleConditionType.employment_duration;
    const isByPeriod =
      ruleData.employment_duration_calc_mode === RuleEmploymentDurationCalcMode.by_period;
    const dateCondition =
      isEmploymentDuration && ruleData.employment_duration_operator
        ? buildDateCondition(
            ruleData.employment_duration_operator as DateConditionOperator,
            isByPeriod
              ? (ruleData.employment_duration_preset ?? RuleEmploymentDurationPresetType.one_month)
              : RuleEmploymentDurationPresetType.custom_date,
            ruleData.employment_duration_date_value ?? null
          )
        : undefined;

    onModified({
      condition_type: ruleData.condition_type,
      condition_entities: ruleData.condition_entities as SelectItem[],
      condition_ids: isEmploymentDuration ? [] : ruleData.condition_ids,
      sign: forceSign ?? (isAffectedEmployeeType ? RuleApplySignType.include : ruleData.sign),
      value_ids: isEmploymentDuration ? [] : ruleData.value_ids,
      value_entities: ruleData.value_entities as SelectItem[],
      date_condition: dateCondition,
    });

    close(id);
  });

  const handleSearch = (text: string | undefined) => {
    switch (selectedConditionType) {
      case RuleConditionType.division:
        divisions.search(text);
        break;
      case RuleConditionType.department:
        departments.search(text);
        break;
      case RuleConditionType.employee:
      case RuleConditionType.employees_with_employee_in_custom_field:
        employee.search(text);
        break;
      case RuleConditionType.location:
        locations.search(text);
        break;
      case RuleConditionType.legal_entity:
        legalEntities.search(text);
        break;
      case RuleConditionType.team:
        teams.search(text);
        break;
    }
  };

  useEffect(() => {
    const subscription = watch((_, { name, type }) => {
      if (name === "condition_type" && type == "change") {
        resetField("condition_entities");
        resetField("condition_ids");
        resetField("value_entities");
        resetField("value_ids");
        resetField("employment_duration_calc_mode");
        resetField("employment_duration_operator");
        resetField("employment_duration_date_value");
        resetField("employment_duration_preset");
        if (rule) {
          rule.condition_entities = [];
          rule.value_entities = [];
        }
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSelect = (_: SelectValueType[], entities: SelectItem[]) => {
    setValue("condition_entities", entities);
  };

  const valueEntities = useWatch({
    control,
    name: "value_entities",
  });
  const conditionEntities = useWatch({
    control,
    name: "condition_entities",
  });
  const selectedConditionEntities = conditionEntities ?? [];
  const selectedValueEntities = valueEntities ?? [];

  useEffect(() => {
    const sub = watch((_, { name }) => {
      if (
        name === "condition_ids" &&
        selectedConditionType === RuleConditionType.custom_field_select
      ) {
        setValue("value_entities", []);
        setValue("value_ids", []);
      }
    });

    return () => sub.unsubscribe();
  }, [watch, setValue, selectedConditionType]);

  const handleSelectValue = (_: SelectValueType[], entities: SelectItem[]) => {
    setValue("value_entities", entities);
  };

  const title = popupTitle ? popupTitle : rule ? t("core:edit_rule") : t("core:create_new_rule");
  const canSelectEntities =
    [
      RuleConditionType.division,
      RuleConditionType.department,
      RuleConditionType.job_profile,
      RuleConditionType.employee,
      RuleConditionType.employment_type,
      RuleConditionType.employment_contract,
      RuleConditionType.work_pattern,
      RuleConditionType.legal_entity,
      RuleConditionType.location,
      RuleConditionType.team,
      RuleConditionType.lifecycle_status,
      RuleConditionType.gender,
      RuleConditionType.custom_field_select,
      RuleConditionType.custom_field_employee_reference,
      RuleConditionType.employees_in_custom_field,
      RuleConditionType.employees_with_employee_in_custom_field,
      RuleConditionType.employees_with_me_in_custom_field,
      RuleConditionType.affected_employee_custom_field_employee_reference,
      RuleConditionType.affected_employee_custom_field_employee_reference_subordinates,
    ].indexOf(selectedConditionType) !== -1;

  const canSelectValues = [
    RuleConditionType.custom_field_select,
    RuleConditionType.employees_with_employee_in_custom_field,
  ].includes(selectedConditionType);

  const canSelectMultipleEntities =
    selectedConditionType === RuleConditionType.employees_with_employee_in_custom_field ||
    !canSelectValues;

  const isConditionMultiSelect = multiSelectConditionTypes.includes(selectedConditionType);

  const canEditEmploymentDuration = selectedConditionType === RuleConditionType.employment_duration;

  const canSearchEntities =
    [
      RuleConditionType.division,
      RuleConditionType.department,
      RuleConditionType.job_profile,
      RuleConditionType.employee,
      RuleConditionType.legal_entity,
      RuleConditionType.location,
      RuleConditionType.team,
      RuleConditionType.lifecycle_status,
      RuleConditionType.gender,
    ].indexOf(selectedConditionType) !== -1;

  const isAffectedEmployeeType =
    [
      RuleConditionType.affected_employee,
      RuleConditionType.affected_employee_direct_manager,
      RuleConditionType.affected_employee_subordinates,
      RuleConditionType.affected_employee_direct_manager_subordinates,
      RuleConditionType.affected_employee_custom_field_employee_reference,
      RuleConditionType.affected_employee_custom_field_employee_reference_subordinates,
    ].indexOf(selectedConditionType) !== -1;

  const usesLocalJobProfileOptions = selectedConditionType === RuleConditionType.job_profile;
  const isCustomFieldReferenceRule = [
    RuleConditionType.employees_in_custom_field,
    RuleConditionType.employees_with_employee_in_custom_field,
    RuleConditionType.employees_with_me_in_custom_field,
  ].includes(selectedConditionType);
  const isCustomFieldReferenceWithSelectedEmployees =
    selectedConditionType === RuleConditionType.employees_with_employee_in_custom_field;

  const watchedSign = useWatch({ control, name: "sign" });
  const watchedValueIds = useWatch({ control, name: "value_ids" });
  const watchedEmpOperator = useWatch({ control, name: "employment_duration_operator" });
  const watchedEmpPreset = useWatch({ control, name: "employment_duration_preset" });
  const watchedEmpDate = useWatch({ control, name: "employment_duration_date_value" });

  const previewDateCondition =
    canEditEmploymentDuration && watchedEmpOperator
      ? buildDateCondition(
          watchedEmpOperator as DateConditionOperator,
          employmentDurationCalcMode === RuleEmploymentDurationCalcMode.by_period
            ? (watchedEmpPreset ?? RuleEmploymentDurationPresetType.one_month)
            : RuleEmploymentDurationPresetType.custom_date,
          watchedEmpDate ?? null
        )
      : undefined;

  const previewRule = {
    condition_type: selectedConditionType,
    condition_ids: canEditEmploymentDuration ? [] : (selectedFieldIds ?? []),
    sign: forceSign ?? watchedSign ?? RuleApplySignType.include,
    value_ids: canEditEmploymentDuration ? [] : (watchedValueIds ?? []),
    value_entities: [],
    condition_entities: [],
    date_condition: previewDateCondition,
  };

  const previewEnabled =
    showEmployeeCountPreview &&
    (!canSelectEntities || (selectedFieldIds?.length ?? 0) > 0 || canEditEmploymentDuration);

  return (
    <Modal
      title={title}
      withCloser={true}
      {...props}
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button type={"submit"} size={"lg"} onClick={onSubmit}>
            {applyLabel ?? t("common:apply")}
          </Button>
          <Button
            variant={"plain"}
            size={"lg"}
            onClick={() => {
              close(id);
            }}
          >
            {t("common:cancel")}
          </Button>
        </Stack>
      }
    >
      <Stack gap={"lg"}>
        <form onSubmit={onSubmit}>
          <Title size={"xs"} header={t("common:scope")} paddingBottom />
          <Controller
            render={({ field: { value, onChange } }) => (
              <Select
                options={conditionTypes}
                emptyTitle={t("common:apply_to")}
                showCaption={true}
                searchable={true}
                value={value}
                onChange={(e) => {
                  onChange(e);
                  setValue("condition_ids", []);
                  setValue("condition_entities", []);
                  setValue("value_ids", []);
                  setValue("value_entities", []);
                  setValue(
                    "employment_duration_calc_mode",
                    RuleEmploymentDurationCalcMode.by_period
                  );
                  setValue("employment_duration_operator", "greater_than");
                  setValue("employment_duration_date_value", null);
                  setValue(
                    "employment_duration_preset",
                    RuleEmploymentDurationPresetType.one_month
                  );
                }}
              />
            )}
            control={control}
            name={`condition_type`}
          />
          {!forceSign && (
            <Controller
              render={({ field: { value, onChange } }) => (
                <Select
                  options={signItems ?? RuleSignItems()}
                  emptyTitle={t("common:condition")}
                  value={value}
                  onChange={onChange}
                  readOnly={isAffectedEmployeeType}
                />
              )}
              control={control}
              name={`sign`}
            />
          )}
          {canEditEmploymentDuration && (
            <Stack>
              <Title size={"xs"} header={t("common:calculation")} paddingBottom paddingTop />
              <Controller
                render={({ field: { value, onChange } }) => (
                  <Select
                    options={RuleEmploymentDurationCalcModeItems()}
                    emptyTitle={t("core:rule_employment_duration_calc_mode")}
                    value={value ?? RuleEmploymentDurationCalcMode.by_period}
                    onChange={(newValue) => {
                      onChange(newValue as RuleEmploymentDurationCalcMode);
                      setValue("employment_duration_operator", "greater_than");
                    }}
                  />
                )}
                control={control}
                name={"employment_duration_calc_mode"}
              />

              {employmentDurationCalcMode === RuleEmploymentDurationCalcMode.by_period && (
                <>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Select
                        options={RuleEmploymentDurationPeriodConditionItems()}
                        emptyTitle={t("core:rule_employment_duration_period_condition")}
                        value={(value ?? "greater_than") as string}
                        error={errors.employment_duration_operator?.message as string}
                        onChange={(newValue) => onChange(newValue as DateConditionOperator)}
                      />
                    )}
                    control={control}
                    name={"employment_duration_operator"}
                  />
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Select
                        options={RuleEmploymentDurationPresetItems()}
                        emptyTitle={t("core:rule_employment_duration_period")}
                        value={value ?? RuleEmploymentDurationPresetType.one_month}
                        onChange={(newValue) =>
                          onChange(newValue as RuleEmploymentDurationPresetType)
                        }
                      />
                    )}
                    control={control}
                    name={"employment_duration_preset"}
                  />
                </>
              )}

              {employmentDurationCalcMode === RuleEmploymentDurationCalcMode.specific_date && (
                <>
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Select
                        options={RuleEmploymentDurationDateConditionItems()}
                        emptyTitle={t("core:rule_employment_duration_date_condition")}
                        value={(value ?? "greater_than") as string}
                        error={errors.employment_duration_operator?.message as string}
                        onChange={(newValue) => onChange(newValue as DateConditionOperator)}
                      />
                    )}
                    control={control}
                    name={"employment_duration_operator"}
                  />
                  <Controller
                    render={({ field: { value, onChange } }) => (
                      <Datepicker
                        value={value ? parseISO(value) : null}
                        onChange={(newValue) =>
                          onChange(newValue ? format(newValue, "yyyy-MM-dd") : null)
                        }
                        label={t("common:date")}
                        required={true}
                        error={errors.employment_duration_date_value?.message as string | undefined}
                        isClearable={false}
                      />
                    )}
                    control={control}
                    name={"employment_duration_date_value"}
                  />
                </>
              )}
            </Stack>
          )}
          {canSelectEntities && (
            <Controller
              render={({ field: { value, onChange } }) => (
                <Multiselect
                  label={
                    isCustomFieldReferenceRule
                      ? t("core:rule_cf_reference_fields_label")
                      : selectedConditionType === RuleConditionType.custom_field_select
                        ? t("core:rule_custom_field_select_field_label")
                        : undefined
                  }
                  emptyTitle={
                    isCustomFieldReferenceRule
                      ? t("core:rule_cf_reference_fields_placeholder")
                      : t("common:select")
                  }
                  values={value}
                  onChange={(selected, entities) => {
                    handleSelect(selected, entities);
                    onChange(selected);
                  }}
                  searchable={canSearchEntities}
                  options={usesLocalJobProfileOptions ? options : rule?.condition_entities}
                  onSearchOptions={usesLocalJobProfileOptions ? undefined : handleSearch}
                  searchOptions={usesLocalJobProfileOptions ? undefined : options}
                  loadOptionsOnOpen={!usesLocalJobProfileOptions}
                  isLoading={usesLocalJobProfileOptions && isLoadingJobProfiles}
                  showCaption={usesLocalJobProfileOptions}
                  captionPerItem={usesLocalJobProfileOptions}
                  isMulti={
                    !singleSelectConditionTypes.includes(selectedConditionType) &&
                    (!isSingleSelect || isConditionMultiSelect) &&
                    canSelectMultipleEntities
                  }
                />
              )}
              control={control}
              name={`condition_ids`}
            />
          )}
          {selectedConditionType === RuleConditionType.employees_with_me_in_custom_field && (
            <Text className="text-secondary text-sm">
              {t("core:rule_cf_reference_role_employee_hint")}
            </Text>
          )}
          {canSelectValues && (
            <Controller
              render={({ field: { value, onChange } }) => (
                <Multiselect
                  label={
                    isCustomFieldReferenceWithSelectedEmployees
                      ? t("core:rule_cf_reference_employees_label")
                      : undefined
                  }
                  emptyTitle={
                    selectedConditionType ===
                    RuleConditionType.employees_with_employee_in_custom_field
                      ? t("core:employees")
                      : t("common:options")
                  }
                  values={value}
                  onChange={(selected, entities) => {
                    handleSelectValue(selected, entities);
                    onChange(selected);
                  }}
                  options={valueEntities}
                  onSearchOptions={handleSearch}
                  searchOptions={valueOptions}
                  loadOptionsOnOpen
                  searchable={
                    selectedConditionType ===
                    RuleConditionType.employees_with_employee_in_custom_field
                  }
                  isLoading={
                    selectedConditionType ===
                      RuleConditionType.employees_with_employee_in_custom_field &&
                    employee.isLoading
                  }
                  showAvatar={
                    selectedConditionType ===
                    RuleConditionType.employees_with_employee_in_custom_field
                  }
                  showCaption={
                    selectedConditionType ===
                    RuleConditionType.employees_with_employee_in_custom_field
                  }
                  isMulti={true}
                />
              )}
              control={control}
              name={`value_ids`}
            />
          )}
          {isCustomFieldReferenceWithSelectedEmployees &&
            selectedConditionEntities.length > 0 &&
            selectedValueEntities.length > 0 && (
              <Text className="text-secondary text-sm">
                {t("core:rule_cf_reference_preview", {
                  fields: selectedConditionEntities.map(({ title }) => `"${title}"`).join(", "),
                  employees: selectedValueEntities.map(({ title }) => title).join(", "),
                })}
              </Text>
            )}
        </form>
        {previewEnabled && <RuleEmployeeCountPreview rule={previewRule} enabled={previewEnabled} />}
        {rule && canDelete && (
          <Stack>
            <hr className="border-dark/10" />
            <ListItem
              title={t("core:delete_rule_question")}
              valueSlot={
                <LinkButtonTo
                  onClick={() => {
                    onDelete();
                    close(id);
                  }}
                  variant={"danger"}
                >
                  {t("common:delete")}
                </LinkButtonTo>
              }
            ></ListItem>
          </Stack>
        )}
      </Stack>
    </Modal>
  );
}
